import {
  type Chain,
  type DynamicClient,
  InvalidParamError,
  UnrecognizedNetworkError,
  type WalletAccount,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
  switchActiveNetworkInLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import {
  type EvmWalletProvider,
  isEvmWalletAccount,
} from '@dynamic-labs-sdk/evm';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';

import { ZERODEV_METADATA } from '../../constants';
import { getAllUserZerodevAddresses } from '../getAllUserZerodevAddresses';
import { getZerodevChainProviderForNetworkId } from '../getZerodevChainProviderForNetworkId';
import { signMessage as signMessageUtils } from '../signMessage';
import { zerodevTransferAmount } from '../zerodevTransferAmount';

export const createZerodevWalletProvider = (
  client: DynamicClient
): EvmWalletProvider => {
  const chain: Chain = 'EVM';
  const walletProviderType = WalletProviderEnum.SmartContractWallet;
  const key = formatWalletProviderKey({
    chain,
    displayName: ZERODEV_METADATA.displayName,
    walletProviderType,
  });

  const getActiveNetworkId = async () =>
    getActiveNetworkIdFromLastKnownRegistry({
      client,
      walletProviderKey: key,
    });

  const getConnectedAddresses = async () => {
    const zerodevAddresses = getAllUserZerodevAddresses(client);

    return {
      addresses: zerodevAddresses,
    };
  };

  const signMessage = async ({
    walletAccount,
    message,
  }: {
    message: string;
    walletAccount?: WalletAccount;
  }) => {
    assertDefined(walletAccount, 'Wallet account is required');

    if (!isEvmWalletAccount(walletAccount)) {
      throw new InvalidParamError(`walletAccount is not an EVM wallet account`);
    }

    return signMessageUtils({ message, walletAccount }, client);
  };

  const switchActiveNetwork = async ({ networkId }: { networkId: string }) => {
    const isNetworkEnabled = getZerodevChainProviderForNetworkId(
      { networkId },
      client
    );

    if (!isNetworkEnabled) {
      throw new UnrecognizedNetworkError({
        networkId,
        originalError: null,
        walletProviderKey: key,
      });
    }

    return switchActiveNetworkInLastKnownRegistry({
      client,
      networkId,
      walletProviderKey: key,
    });
  };

  return {
    chain,
    getActiveNetworkId,
    getConnectedAddresses,
    groupKey: formatWalletProviderGroupKey(ZERODEV_METADATA.displayName),
    key,
    metadata: {
      displayName: ZERODEV_METADATA.displayName,
      icon: ZERODEV_METADATA.icon,
    },
    signMessage,
    switchActiveNetwork,
    transferAmount: (args) => zerodevTransferAmount(args, client),
    walletProviderType,
  };
};
